import { Link } from "react-router-dom";
import { LogOut, RefreshCcw } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { api, type Me } from "@/lib/api";
import { toast } from "sonner";

interface HeaderProps {
  me: Me | null;
  onMeChange: (me: Me | null) => void;
}

export function Header({ me, onMeChange }: HeaderProps) {
  async function handleSync() {
    const t = toast.loading("Syncing your Steam library...");
    try {
      const result = await api.syncLibrary();
      toast.success(`Synced ${result.count} games`, { id: t });
      const fresh = await api.me();
      onMeChange(fresh);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sync failed", { id: t });
    }
  }

  async function handleLogout() {
    try {
      await api.logout();
      onMeChange(null);
      toast.success("Signed out");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Logout failed");
    }
  }

  return (
    <header className="border-b border-border/60 bg-background/40 backdrop-blur">
      <div className="container flex h-14 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-semibold">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-primary/20 text-primary">
            <span className="block h-3 w-3 rounded-full bg-primary" />
          </span>
          <span>Steam Game Wheel</span>
        </Link>
        {me ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSync}
              title="Resync your Steam library"
            >
              <RefreshCcw />
              <span className="hidden sm:inline">Resync</span>
            </Button>
            <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card/40 px-2 py-1">
              <Avatar className="h-7 w-7">
                {me.avatarUrl && <AvatarImage src={me.avatarUrl} alt={me.displayName} />}
                <AvatarFallback>{me.displayName.slice(0, 2).toUpperCase()}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{me.displayName}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} title="Sign out">
              <LogOut />
            </Button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
